// Section 24.8: the live projection. project(game, draft, 4) runs the real phases in projection
// mode; it is recomputed 60 ms after the last draft change, in an idle callback, and the last
// result stays visible (greyed) while recomputing. Results are cached by (run, turn, draft).
// The right panel reads season 1; the Jobs tab reads all four and a baseline with no changes.
import { effect, signal, untracked } from '@preact/signals';
import { emptyDecision, project, type Decision, type GameState, type Projection } from '../sim/api';
import { cfgOption, currentRun } from './run';
import { draft, game, status } from './state';

export interface ProjectionState {
  /** game.turn the result belongs to, or -1. */
  turn: number;
  /** A recompute is scheduled or running; show the last result greyed. */
  pending: boolean;
  /** Under the current draft. */
  result: Projection | null;
  /** Under an empty decision (no changes this season), for the Section 8 dip preview. */
  baseline: Projection | null;
  error: string | null;
}

export const PROJECTION_DEBOUNCE_MS = 60;
export const PROJECTION_HORIZON = 4;
const CACHE_SIZE = 16;

export const projection = signal<ProjectionState>({
  turn: -1,
  pending: false,
  result: null,
  baseline: null,
  error: null,
});

const cache = new Map<string, Projection>();
let timer: ReturnType<typeof setTimeout> | null = null;

function cached(key: string, make: () => Projection): Projection {
  const hit = cache.get(key);
  if (hit !== undefined) {
    cache.delete(key);
    cache.set(key, hit);
    return hit;
  }
  const made = make();
  cache.set(key, made);
  while (cache.size > CACHE_SIZE) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
  return made;
}

function keyOf(g: GameState, d: Decision | null): string {
  return `${currentRun().runId}|${g.turn}|${g.rngState}|${d === null ? '-' : JSON.stringify(d)}`;
}

function projectable(g: GameState | null): g is GameState {
  const s = status.peek();
  return (
    g !== null &&
    g.phaseCursor === null &&
    (g.outcome === null || g.sandbox) &&
    (s === 'deciding' || s === 'reporting')
  );
}

/** Compute now (tests, and the idle callback). */
export function flushProjection(): void {
  if (timer !== null) clearTimeout(timer);
  timer = null;
  const g = game.peek();
  const d = draft.peek();
  if (!projectable(g)) {
    projection.value = { turn: -1, pending: false, result: null, baseline: null, error: null };
    return;
  }
  const opts = cfgOption();
  try {
    const baseline = cached(keyOf(g, null), () =>
      project(g, emptyDecision(g), PROJECTION_HORIZON, opts),
    );
    const result =
      d === null
        ? baseline
        : cached(keyOf(g, d), () => project(g, d, PROJECTION_HORIZON, opts));
    projection.value = { turn: g.turn, pending: false, result, baseline, error: null };
  } catch (error) {
    // A projection failure never touches the game (it is advisory); show it and move on.
    projection.value = {
      ...projection.peek(),
      pending: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

type IdleWindow = { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };

function whenIdle(run: () => void): void {
  const idle = (globalThis as IdleWindow).requestIdleCallback;
  if (idle === undefined) run();
  else idle(run, { timeout: 200 });
}

/** Debounce a recompute; a cached result applies at once. */
function scheduleProjection(g: GameState | null, d: Decision | null): void {
  if (!projectable(g)) {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    if (projection.peek().result !== null)
      projection.value = { turn: -1, pending: false, result: null, baseline: null, error: null };
    return;
  }
  const hit = cache.get(keyOf(g, d));
  const base = cache.get(keyOf(g, null));
  if (hit !== undefined && base !== undefined) {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    projection.value = { turn: g.turn, pending: false, result: hit, baseline: base, error: null };
    return;
  }
  if (!projection.peek().pending) projection.value = { ...projection.peek(), pending: true };
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    whenIdle(flushProjection);
  }, PROJECTION_DEBOUNCE_MS);
}

/** Any change to the game, the draft or the status schedules a recompute. */
effect(() => {
  const g = game.value;
  const d = draft.value;
  if (status.value === 'resolving') return;
  untracked(() => {
    scheduleProjection(g, d);
  });
});
